import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import CreateBookingModal from './CreateBookingModal';
import '../../styles/components/layout/ChatWindow.css';
import 'react-datepicker/dist/react-datepicker.css';
import { stripePromise } from '../../context/stripeContext';
import { formatDisplayName } from '../../utils/nameFormatting';

const STORE_URL = 'https://3mv40ugihi.execute-api.eu-west-2.amazonaws.com/dev/storeMessage';
const BOOKINGS_URL = 'https://gp64xgt52i.execute-api.eu-west-2.amazonaws.com/dev/bookings';
const PAY_URL = 'https://gp64xgt52i.execute-api.eu-west-2.amazonaws.com/dev/pay';

const STATUS_LABELS = {
  requested: 'Awaiting approval',
  accepted: 'Accepted',
  awaiting_payment: 'Awaiting payment',
  confirmed: 'Confirmed',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

const ChatWindow = ({
  onClose,
  recipientUserSub,
  chatPartnerName,
  embedded = false,
}) => {
  const { userAttributes, getAccessToken } = useAuth();
  const {
    webSocketRef,
    openChats,
    setOpenChats,
    refreshBookingById,
  } = useWebSocket();

  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingToRevise, setBookingToRevise] = useState(null);

  const chatMessages = (openChats ?? []).find(
    (chat) => chat.recipientUserSub === recipientUserSub
  )?.messages ?? [];

  const visibleBookingIds = useMemo(
    () => [
      ...new Set(
        chatMessages
          .filter(
            (item) =>
              item.isBooking &&
              item.bookingDetails?.id
          )
          .map((item) => item.bookingDetails.id)
      ),
    ],
    [chatMessages]
  );

  const visibleBookingIdsKey = visibleBookingIds.join('|');

  useEffect(() => {
    if (!visibleBookingIds.length) {
      return undefined;
    }

    const refreshVisibleBookings = () => {
      visibleBookingIds.forEach((bookingId) => {
        refreshBookingById(bookingId).catch((error) => {
          console.error(
            '[ChatWindow] Failed to refresh booking:',
            error
          );
        });
      });
    };

    refreshVisibleBookings();
    window.addEventListener('focus', refreshVisibleBookings);

    return () => {
      window.removeEventListener('focus', refreshVisibleBookings);
    };
  }, [
    recipientUserSub,
    visibleBookingIdsKey,
    refreshBookingById,
  ]);

  const toIso = (date, hhmm) => {
    if (!hhmm) {
      return new Date(date).toISOString();
    }

    const [hours, minutes] = hhmm.split(':').map(Number);
    const value = new Date(date);
    value.setHours(hours, minutes, 0, 0);

    return value.toISOString();
  };

  const upsertChatMessage = (msg) => {
    setOpenChats((prev) => {
      const chats = Array.isArray(prev) ? prev : [];
      const index = chats.findIndex(
        (chat) => chat.recipientUserSub === recipientUserSub
      );

      if (index === -1) {
        return [
          ...chats,
          {
            recipientUserSub,
            chatPartnerName,
            messages: [msg],
          },
        ];
      }

      return chats.map((chat, i) =>
        i === index
          ? {
              ...chat,
              messages: [...(chat.messages ?? []), msg],
            }
          : chat
      );
    });
  };

  const updateBookingInChat = (booking) => {
    setOpenChats((prev) =>
      (Array.isArray(prev) ? prev : []).map((chat) => ({
        ...chat,
        messages: (chat.messages ?? []).map((msg) =>
          msg.isBooking && msg.bookingDetails?.id === booking.id
            ? {
                ...msg,
                bookingDetails: booking,
              }
            : msg
        ),
      }))
    );
  };

  const notifyBookingUpdate = (booking) => {
    const mySub = userAttributes?.sub;
    const otherUser =
      mySub === booking.client_id
        ? booking.photographer_id
        : booking.client_id;

    if (
      otherUser &&
      webSocketRef.current?.readyState === WebSocket.OPEN
    ) {
      webSocketRef.current.send(
        JSON.stringify({
          action: 'sendBooking',
          recipientUserSub: otherUser,
          bookingId: booking.id,
        })
      );
    }
  };

  const bookingFormBody = (details) => {
    const allDayStart = new Date(details.date);
    const allDayEnd = new Date(details.date);

    if (details.allDay) {
      allDayStart.setHours(0, 0, 0, 0);
      allDayEnd.setHours(0, 0, 0, 0);
      allDayEnd.setDate(allDayEnd.getDate() + 1);
    }

    return {
      start_time: details.allDay
        ? allDayStart.toISOString()
        : toIso(details.date, details.startTime),
      end_time: details.allDay
        ? allDayEnd.toISOString()
        : toIso(details.date, details.endTime),
      all_day: details.allDay,
      address: details.address,
      event_name: details.eventName,
      shoot_type: details.shootType,
      price_cents: Math.round(Number(details.price) * 100),
      contact_phone: details.contactPhone,
      payment_terms: details.paymentTerms,
    };
  };

  const handleBookingSubmit = async (details) => {
    try {
      const jwt = await getAccessToken();

      if (bookingToRevise) {
        const response = await fetch(
          `${BOOKINGS_URL}/${encodeURIComponent(bookingToRevise.id)}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              action: 'revise',
              ...bookingFormBody(details),
            }),
          }
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok || !payload.booking) {
          throw new Error(
            payload?.error || `HTTP ${response.status}`
          );
        }

        updateBookingInChat(payload.booking);
        notifyBookingUpdate(payload.booking);
        setBookingToRevise(null);
        setIsBookingModalOpen(false);
        return;
      }

      const apiBody = {
        client_id: recipientUserSub,
        ...bookingFormBody(details),
      };

      const response = await fetch(BOOKINGS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(apiBody),
      });

      const payload = await response.json().catch(() => ({}));

      if (response.status === 409) {
        alert(payload?.error || 'Slot already taken');
        return;
      }

      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }

      const bookingId = payload.booking_id;
      const bookingDetails = payload.booking;

      if (!bookingId || !bookingDetails) {
        throw new Error(
          'Booking created but authoritative booking details were not returned'
        );
      }

      const readable =
        `${bookingDetails.photographerName} sent a booking proposal ` +
        `(id #${bookingId.slice(0, 8)}…) for ${bookingDetails.eventName} – ` +
        `${new Date(bookingDetails.date).toLocaleDateString('en-GB')} at £${bookingDetails.price}`;

      const storeResponse = await fetch(STORE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          participants: [
            userAttributes.sub,
            recipientUserSub,
          ],
          message: readable,
          booking_id: bookingId,
        }),
      });

      if (!storeResponse.ok) {
        console.warn(
          'Booking was created but its chat reference could not be persisted',
          storeResponse.status
        );
      }

      notifyBookingUpdate(bookingDetails);

      upsertChatMessage({
        text: readable,
        sent: true,
        isBooking: true,
        bookingDetails,
      });

      setIsBookingModalOpen(false);
    } catch (error) {
      console.error(error);
      alert(error.message || String(error));
    }
  };

  const handleSubmitMessage = async (event) => {
    event.preventDefault();

    const trimmed = message.trim();
    if (!trimmed) {
      return;
    }

    upsertChatMessage({
      text: trimmed,
      sent: true,
    });

    const jwt = await getAccessToken();

    await fetch(STORE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        participants: [
          userAttributes.sub,
          recipientUserSub,
        ],
        message: trimmed,
      }),
    }).catch(console.error);

    if (
      webSocketRef.current?.readyState === WebSocket.OPEN
    ) {
      webSocketRef.current.send(
        JSON.stringify({
          action: 'sendMessage',
          message: trimmed,
          recipientUserSub,
        })
      );
    } else {
      console.error('WS not open - message not sent live');
    }

    setMessage('');
  };

  const updateBookingStatus = async (booking, action) => {
    const jwt = await getAccessToken();

    const response = await fetch(
      `${BOOKINGS_URL}/${encodeURIComponent(booking.id)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ action }),
      }
    );

    const payload = await response.json().catch(() => ({}));

    if (!response.ok || !payload.booking) {
      throw new Error(
        payload?.error || `HTTP ${response.status}`
      );
    }

    updateBookingInChat(payload.booking);
    notifyBookingUpdate(payload.booking);

    return payload.booking;
  };

  const handlePay = async (booking) => {
    try {
      const jwt = await getAccessToken();

      const response = await fetch(PAY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          booking_id: booking.id,
          return_origin: window.location.origin,
        }),
      });

      const text = await response.text();

      if (!text) {
        throw new Error(
          `Empty response from /pay (HTTP ${response.status})`
        );
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Bad JSON from /pay: ${text}`);
      }

      let sessionId = data.sessionId;

      if (!sessionId && typeof data.output === 'string') {
        const inner = JSON.parse(data.output);
        sessionId =
          inner?.Payload?.sessionId ||
          inner?.sessionId ||
          null;
      }

      if (!response.ok || !sessionId) {
        throw new Error(
          data?.error ||
          `No sessionId in /pay response (HTTP ${response.status})`
        );
      }

      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error(error);
      alert(error.message || String(error));
    }
  };

  const handleAcceptAndPay = async (booking) => {
    try {
      const accepted = await updateBookingStatus(booking, 'accept');
      await handlePay(accepted);
    } catch (error) {
      console.error(error);
      alert(error.message || String(error));
    }
  };

  const handleDecline = async (booking) => {
    try {
      await updateBookingStatus(booking, 'decline');
    } catch (error) {
      console.error(error);
      alert(error.message || String(error));
    }
  };

  const handleWithdraw = async (booking) => {
    if (!window.confirm('Withdraw this booking proposal?')) {
      return;
    }

    try {
      await updateBookingStatus(booking, 'withdraw');
    } catch (error) {
      console.error(error);
      alert(error.message || String(error));
    }
  };

  const handleRevise = (booking) => {
    setBookingToRevise(booking);
    setIsBookingModalOpen(true);
  };

  const nextOutstandingPayment = (booking) =>
    (booking.paymentSchedule || []).find(
      (row) => !['paid', 'cancelled'].includes(row.status)
    );

  const BookingCard = ({ booking, viewerIsClient, viewerIsPhotographer }) => {
    const prettyDate = new Date(
      booking.date
    ).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const outstanding = nextOutstandingPayment(booking);
    const hasOutstanding = Boolean(outstanding);
    const hasSuccessfulPayment =
      (booking.paymentSchedule || []).some(
        (row) => row.status === 'paid'
      );
    const canPhotographerEdit =
      viewerIsPhotographer &&
      !hasSuccessfulPayment &&
      ['requested', 'accepted'].includes(booking.status);

    const canPhotographerWithdraw =
      viewerIsPhotographer &&
      !hasSuccessfulPayment &&
      ['requested', 'accepted'].includes(booking.status);

    const canClientPay =
      viewerIsClient &&
      hasOutstanding &&
      ['accepted', 'awaiting_payment', 'confirmed'].includes(booking.status);

    const paymentActionLabel =
      outstanding?.type === 'balance'
        ? `Pay balance £${outstanding.amount}`
        : outstanding?.type === 'deposit'
        ? `Pay deposit £${outstanding.amount}`
        : outstanding
        ? `Pay £${outstanding.amount}`
        : 'Pay';

    return (
      <div className="booking-message">
        <div className="booking-message__header">
          <div>
            <span className="booking-message__eyebrow">
              {booking.status === 'confirmed' || booking.status === 'completed'
                ? 'Booking'
                : 'Booking proposal'}
            </span>
            <h4>{booking.eventName}</h4>
          </div>

          <span className={`booking-status booking-status--${booking.status}`}>
            {STATUS_LABELS[booking.status] || booking.status}
          </span>
        </div>

        <p className="booking-message__intro">
          <strong>{formatDisplayName(booking.photographerName)}</strong> proposed these booking terms.
        </p>

        <dl className="booking-message__details">
          <div>
            <dt>Date</dt>
            <dd>{prettyDate}</dd>
          </div>

          <div>
            <dt>Time</dt>
            <dd>
              {booking.allDay
                ? 'All day'
                : `${booking.start} to ${booking.end}`}
            </dd>
          </div>

          <div>
            <dt>Total</dt>
            <dd>£{booking.price}</dd>
          </div>

          <div>
            <dt>Location</dt>
            <dd title={booking.address || undefined}>
              {booking.address || 'Not specified'}
            </dd>
          </div>
        </dl>

        {(booking.paymentSchedule || []).length > 0 && (
          <div className="booking-payment-schedule">
            <span className="booking-payment-schedule__title">Payment terms</span>

            {(booking.paymentSchedule || []).map((row) => (
              <div key={row.id} className="booking-payment-row">
                <div>
                  <strong>
                    {row.type === 'deposit'
                      ? 'Deposit'
                      : row.type === 'balance'
                      ? 'Balance'
                      : row.type === 'full_payment'
                      ? 'Full payment'
                      : 'Additional charge'}
                  </strong>

                  {row.dueAt && row.type === 'balance' && (
                    <small>
                      Due {new Date(row.dueAt).toLocaleDateString('en-GB')}
                    </small>
                  )}
                </div>

                <span>£{row.amount}</span>
                <span className={`payment-row-status payment-row-status--${row.status}`}>
                  {row.status === 'paid' ? 'Paid' : 'Outstanding'}
                </span>
              </div>
            ))}

            <div className="booking-payment-summary">
              <span>
                Paid £{((booking.paymentSummary?.paid_cents || 0) / 100).toFixed(2)}
              </span>
              <span>
                Outstanding £{((booking.paymentSummary?.outstanding_cents || 0) / 100).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {booking.status === 'requested' && viewerIsClient && (
          <div className="booking-actions">
            <button
              className="confirm-booking-btn"
              onClick={() => handleAcceptAndPay(booking)}
            >
              {outstanding
                ? `Accept & pay £${outstanding.amount}`
                : 'Accept'}
            </button>

            <button
              className="cancel-booking-btn"
              onClick={() => handleDecline(booking)}
            >
              Decline
            </button>
          </div>
        )}

        {canClientPay && (
          <div className="booking-actions">
            <button
              className="confirm-booking-btn"
              onClick={() => handlePay(booking)}
            >
              {paymentActionLabel}
            </button>
          </div>
        )}

        {(canPhotographerEdit || canPhotographerWithdraw) && (
          <div className="booking-actions">
            {canPhotographerEdit && (
              <button
                className="secondary-booking-btn"
                onClick={() => handleRevise(booking)}
              >
                Revise
              </button>
            )}

            {canPhotographerWithdraw && (
              <button
                className="cancel-booking-btn"
                onClick={() => handleWithdraw(booking)}
              >
                Withdraw
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const toggleMinimize = () =>
    setIsMinimized((value) => !value);

  const minimized = embedded ? false : isMinimized;
  const displayPartnerName = formatDisplayName(chatPartnerName);

  return (
    <div
      className={`chat-window${embedded ? ' chat-window--embedded' : ''}`}
      style={embedded ? undefined : {
        height: minimized ? '60px' : '400px',
      }}
    >
      <div className="chat-header">
        <span className="chat-partner-name">
          {displayPartnerName}
        </span>

        {!embedded && (
          <div className="chat-header-actions">
            <button
              onClick={toggleMinimize}
              className="chat-icon-btn minimize-btn"
              aria-label={minimized ? 'Expand conversation' : 'Minimize conversation'}
            >
              {minimized ? '⌃' : '−'}
            </button>

            <button
              className="chat-icon-btn close-btn"
              onClick={onClose}
              aria-label="Close conversation"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {userAttributes?.['custom:userRole'] === 'photographer' &&
        !minimized && (
          <div className="create-booking-row">
            <button
              type="button"
              className="create-booking-button"
              onClick={() => {
                setBookingToRevise(null);
                setIsBookingModalOpen(true);
              }}
            >
              Create booking
            </button>
          </div>
        )}

      {!minimized && (
        <div className="chat-content">
          <div className="messages-display-area">
            {chatMessages.map((item, index) => {
              if (
                item.isBooking &&
                item.bookingDetails
              ) {
                const viewerIsClient =
                  userAttributes.sub ===
                  item.bookingDetails.client_id;
                const viewerIsPhotographer =
                  userAttributes.sub ===
                  item.bookingDetails.photographer_id;

                return (
                  <BookingCard
                    key={item.bookingDetails.id || index}
                    booking={item.bookingDetails}
                    viewerIsClient={viewerIsClient}
                    viewerIsPhotographer={viewerIsPhotographer}
                  />
                );
              }

              const cls =
                `message-bubble ${item.sent ? 'message-sent' : 'message-received'}`;

              return (
                <div
                  key={index}
                  className={cls}
                >
                  {item.text}
                </div>
              );
            })}
          </div>

          <form
            onSubmit={handleSubmitMessage}
            className="message-form"
          >
            <input
              value={message}
              onChange={(event) =>
                setMessage(event.target.value)
              }
              placeholder="Type a message..."
            />

            <button type="submit">
              Send
            </button>
          </form>
        </div>
      )}

      {isBookingModalOpen && (
        <CreateBookingModal
          recipientName={displayPartnerName}
          initialBooking={bookingToRevise}
          onClose={() => {
            setBookingToRevise(null);
            setIsBookingModalOpen(false);
          }}
          onSubmit={handleBookingSubmit}
        />
      )}
    </div>
  );
};

export default ChatWindow;